/**
 * The dashboard's words and its three limits.
 *
 * The front page answers "how much do I hold, and what has moved" for a
 * collector who has just signed in; these are the labels on its tiles, its
 * strip and its activity panel. What the numbers count and how the rows are
 * built is `dashboard.model.ts`, and the reads are
 * `useDashboardController.ts`.
 *
 * The limits are budgets rather than page sizes: how many works the strip
 * shows, how many activity rows, and how many collection feeds are worth a
 * request beside the personal one.
 */

export const DASHBOARD_COPY = {
  loading: "Gathering your registry…",
  worksRegistered: "Works registered",
  onLoan: "On loan",
  /** A work with at least one live verification on its chain of title. */
  verified: "Verified works",
  collections: "Collections",
  /** Labels the collection switcher for a screen reader; the value is the name. */
  collectionLabel: "Collection",
  recentActivity: "Recent Activity",
  quickActions: "Quick actions",
  viewAll: "View all",
  register: "Register new artwork",
  openCollection: "Open collections",
  empty:
    "Nothing registered yet. Registering a work sends it to the authority for the artist, and it joins the registry when they approve it.",
  /**
   * The panel is scoped to one collection, so an empty strip beside a
   * non-zero WORKS tile is not a contradiction — it is this collection being
   * empty while another holds them. Saying so is what stops it reading as a
   * screen that failed to load.
   */
  emptyCollection: "Nothing in this collection yet.",
  /**
   * There is deliberately no valuation figure and no 30-day delta on this
   * screen. The prototype has both; ONDO stores neither, and a dashboard that
   * invents a number is worse than one that shows fewer.
   *
   * For the same reason there is no EVENTS LOGGED tile, which the design has:
   * there is no event stream to count. The layout is the design's; the numbers
   * in it are only the ones the registry can answer.
   *
   * AGENTS ASSIGNED was on that list until agent mandates landed, and is now a
   * tile that COULD be built — `ondo.work_agents()` answers per work, so a count
   * across a holding is a sum this screen can reach. It is left out for a
   * narrower reason than "no data": the number a collector wants is how many of
   * their works are represented, and that is a question about a collection,
   * which this panel does not have. It belongs beside the collection grid.
   */
} as const;

/**
 * How many collections' feeds the activity panel will read.
 *
 * `GET /v1/activity` takes one subject, and `fetchCollections()` returns every
 * collection the caller holds any grant on — a gallery member could hold dozens.
 * This NARROWS where the panel's earlier, provenance-derived version had to DROP:
 * that one refused to sample because `WorkSummary` carries no timestamp, so "the
 * most recent works" was not a set the client could choose. `CollectionSummary`
 * carries `createdAt`, so "the newest collections" is one it can.
 *
 * What replaces this is not a bigger number. It is having a principal's `user:`
 * feed FOLLOW each collection feed as grants are made, which turns the whole
 * panel into a single paginated read — a Stream provisioning change, not a
 * client one.
 */
export const ACTIVITY_COLLECTION_BUDGET = 12;

/** How many rows the activity panel shows, newest first. */
export const ACTIVITY_ROW_LIMIT = 8;

/** How many works the collection strip shows before "View all". */
export const COLLECTION_STRIP_LIMIT = 3;

